import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { MdOutlineRemoveRedEye, MdOutlineSecurity } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import DefaultAuth from 'layouts/auth/variants/Default';

type SignInFormValues = {
  Tipo: string;
  CorreoElectronico: string;
  Password: string;
  remember: boolean;
};

const accountTypes = [
  { id: '1', label: 'Administrador' },
  { id: '2', label: 'Candidato' },
  { id: '3', label: 'Comitiva' },
];

const loginRoutes: Record<string, string> = {
  '1': '/login_admin',
  '2': '/login_canidato',
  '3': '/login_personal',
};

function buildAuthCookie(token: string, remember: boolean) {
  const cookieParts = [`token=${token}`, 'path=/'];
  const hostname = window.location.hostname;
  const isHttps = window.location.protocol === 'https:';
  const isToguiDomain =
    hostname === 'togui.com.mx' || hostname.endsWith('.togui.com.mx');

  if (isHttps) {
    cookieParts.push('SameSite=None', 'Secure');
  } else {
    cookieParts.push('SameSite=Lax');
  }

  if (isToguiDomain) {
    cookieParts.push('domain=.togui.com.mx');
  }

  if (remember) {
    cookieParts.push(`max-age=${60 * 60 * 24 * 30}`);
  }

  return cookieParts.join('; ');
}

function SignIn() {
  const toast = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);

  const textColor = useColorModeValue('navy.700', 'white');
  const mutedText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const panelBg = useColorModeValue('whiteAlpha.900', 'navy.800');
  const panelBorder = useColorModeValue('whiteAlpha.700', 'whiteAlpha.200');
  const accentBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const accentColor = useColorModeValue('brand.500', 'brand.200');
  const pageHint = useColorModeValue('secondaryGray.500', 'secondaryGray.300');

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormValues>({
    defaultValues: {
      Tipo: '',
      CorreoElectronico: '',
      Password: '',
      remember: true,
    },
  });

  const onSubmit = async (values: SignInFormValues) => {
    const endpoint = loginRoutes[values.Tipo];
    const serverUrl = process.env.REACT_APP_URL_SERVER?.trim() || 'http://localhost:3000';

    if (!endpoint) {
      toast({
        title: 'Selecciona un tipo de cuenta',
        status: 'warning',
      });
      return;
    }

    const formData = new FormData();
    formData.append('Tipo', values.Tipo);
    formData.append('CorreoElectronico', values.CorreoElectronico.trim());
    formData.append('Password', values.Password);

    const loadingToast = toast({
      title: 'Iniciando sesion',
      description: 'Validando credenciales y perfil de acceso.',
      status: 'loading',
      duration: null,
    });

    try {
      const response = await fetch(`${serverUrl}${endpoint}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const rawBody = await response.text();
      let payload: { token?: string; message?: string } = {};

      if (rawBody) {
        try {
          payload = JSON.parse(rawBody);
        } catch {
          payload = { message: rawBody };
        }
      }

      if (!response.ok || !payload.token) {
        throw new Error(payload.message || 'Las credenciales no fueron aceptadas.');
      }

      document.cookie = buildAuthCookie(payload.token, values.remember);

      toast.close(loadingToast);
      toast({
        title: 'Sesion iniciada',
        description: 'Redirigiendo al panel principal.',
        status: 'success',
      });

      navigate('/admin');
      window.location.reload();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Ocurrio un error al intentar iniciar sesion.';

      toast.close(loadingToast);
      toast({
        title: 'No fue posible iniciar sesion',
        description: message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <DefaultAuth>
      <Flex
        maxW={{ base: '100%', md: '480px' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        minH="100%"
        alignItems="center"
        justifyContent="center"
        px={{ base: '20px', md: '0px' }}
        py={{ base: '32px', md: '0px' }}
      >
        <Box
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          w="100%"
          bg={panelBg}
          border="1px solid"
          borderColor={panelBorder}
          borderRadius="28px"
          backdropFilter="blur(18px)"
          boxShadow="0 30px 80px rgba(16, 24, 40, 0.14)"
          p={{ base: 6, md: 8 }}
        >
          <Stack spacing={6}>
            <Flex
              align="center"
              gap={3}
              w="fit-content"
              px={4}
              py={2}
              borderRadius="full"
              bg={accentBg}
              color={accentColor}
            >
              <Icon as={MdOutlineSecurity} boxSize={5} />
              <Text fontSize="sm" fontWeight="700">
                Acceso seguro
              </Text>
            </Flex>

            <Box>
              <Heading color={textColor} fontSize={{ base: '30px', md: '38px' }} lineHeight="1.1">
                Inicia sesion
              </Heading>
              <Text mt={3} color={mutedText} fontSize="md">
                Ingresa tu correo y contrasena para entrar al panel electoral.
              </Text>
            </Box>

            <Box
              borderRadius="20px"
              bg={accentBg}
              px={4}
              py={3}
              border="1px solid"
              borderColor={panelBorder}
            >
              <Text color={pageHint} fontSize="sm">
                Usa el tipo de cuenta correcto para que el backend dirija el acceso al endpoint
                correspondiente.
              </Text>
            </Box>

            <Stack spacing={5}>
              <FormControl isInvalid={!!errors.Tipo}>
                <FormLabel color={textColor} fontSize="sm" fontWeight="700">
                  Tipo de cuenta
                </FormLabel>
                <Select
                  placeholder="Selecciona el perfil"
                  variant="main"
                  h="50px"
                  borderRadius="16px"
                  {...register('Tipo', { required: 'Selecciona un tipo de cuenta.' })}
                >
                  {accountTypes.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.Tipo?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.CorreoElectronico}>
                <FormLabel color={textColor} fontSize="sm" fontWeight="700">
                  Correo electronico
                </FormLabel>
                <Input
                  variant="auth"
                  type="email"
                  placeholder="usuario@dominio.com"
                  size="lg"
                  borderRadius="16px"
                  {...register('CorreoElectronico', {
                    required: 'El correo es obligatorio.',
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: 'Ingresa un correo valido.',
                    },
                  })}
                />
                <FormErrorMessage>{errors.CorreoElectronico?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.Password}>
                <FormLabel color={textColor} fontSize="sm" fontWeight="700">
                  Contrasena
                </FormLabel>
                <InputGroup size="lg">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    variant="auth"
                    borderRadius="16px"
                    pr="50px"
                    {...register('Password', {
                      required: 'La contrasena es obligatoria.',
                    })}
                  />
                  <InputRightElement h="full">
                    <Icon
                      as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                      color={mutedText}
                      boxSize={5}
                      cursor="pointer"
                      onClick={() => setShowPassword((current) => !current)}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.Password?.message}</FormErrorMessage>
              </FormControl>

              <Flex justify="space-between" align={{ base: 'start', sm: 'center' }} gap={4} wrap="wrap">
                <Checkbox colorScheme="brandScheme" {...register('remember')}>
                  <Text color={textColor} fontSize="sm">
                    Mantener la sesion activa
                  </Text>
                </Checkbox>
                <Text color={pageHint} fontSize="sm">
                  URL API: {process.env.REACT_APP_URL_SERVER || 'http://localhost:3000'}
                </Text>
              </Flex>
            </Stack>

            <Button
              type="submit"
              variant="brand"
              fontWeight="700"
              h="54px"
              borderRadius="18px"
              isLoading={isSubmitting}
              loadingText="Ingresando"
            >
              Ingresar al sistema
            </Button>
          </Stack>
        </Box>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
